import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react"
import { useRouter } from "next/router"

import { useOrganization } from "@clerk/nextjs"
import { PostHogProvider, usePostHog } from "posthog-js/react"

import { useAnalytics } from "@/lib/analytics"

import posthog, { setPostHogPageTemplate } from "../config/posthog"

interface OrganizationFeatureFlagScope {
  organizationId: string | null
  hasError: boolean
  isReady: boolean
}

const OrganizationFeatureFlagScopeContext =
  createContext<OrganizationFeatureFlagScope>({
    organizationId: null,
    hasError: false,
    isReady: false,
  })

function PostHogPageTemplateTracker() {
  const router = useRouter()

  useEffect(() => {
    setPostHogPageTemplate(router.pathname)

    const handleRouteChangeComplete = () => {
      setPostHogPageTemplate(router.pathname)
    }

    router.events.on("routeChangeComplete", handleRouteChangeComplete)

    return () => {
      router.events.off("routeChangeComplete", handleRouteChangeComplete)
    }
  }, [router.events, router.pathname])

  return null
}

function OrganizationFeatureFlagScopeProvider({
  children,
}: {
  children: ReactNode
}) {
  const posthogClient = usePostHog()
  const { organization } = useOrganization()
  const { group } = useAnalytics()
  const organizationId = organization?.id ?? null
  const organizationName = organization?.name
  const organizationSlug = organization?.slug
  const [readyOrganizationId, setReadyOrganizationId] = useState<string | null>(
    null,
  )
  const [failedOrganizationId, setFailedOrganizationId] = useState<
    string | null
  >(null)

  useEffect(() => {
    setReadyOrganizationId(null)
    setFailedOrganizationId(null)
    if (!posthogClient || !organizationId) {
      return
    }

    let groupTransitionStarted = false
    let successfulCallbacksSinceGroup = 0
    const unsubscribe = posthogClient.onFeatureFlags(
      (_flags, _variants, context) => {
        if (
          !groupTransitionStarted ||
          posthogClient.getGroups().organization !== organizationId
        ) {
          return
        }

        if (context?.errorsLoading) {
          setFailedOrganizationId(organizationId)
          return
        }

        setFailedOrganizationId((failedId) =>
          failedId === organizationId ? null : failedId,
        )

        successfulCallbacksSinceGroup += 1
        if (successfulCallbacksSinceGroup === 1) {
          // PostHog serializes flag requests, so this confirmation reload runs
          // after any response that was already in flight for the previous org.
          posthogClient.reloadFeatureFlags()
          return
        }
        setReadyOrganizationId(organizationId)
      },
    )

    group("organization", organizationId, {
      name: organizationName,
      slug: organizationSlug,
    })
    groupTransitionStarted = true
    posthogClient.reloadFeatureFlags()

    return () => {
      unsubscribe()
    }
  }, [group, organizationId, organizationName, organizationSlug, posthogClient])

  const scope = useMemo(
    () => ({
      organizationId,
      hasError:
        organizationId !== null && organizationId === failedOrganizationId,
      isReady:
        organizationId !== null &&
        (process.env.NODE_ENV !== "production" ||
          organizationId === readyOrganizationId),
    }),
    [failedOrganizationId, organizationId, readyOrganizationId],
  )

  return (
    <OrganizationFeatureFlagScopeContext.Provider value={scope}>
      {children}
    </OrganizationFeatureFlagScopeContext.Provider>
  )
}

export function useOrganizationFeatureFlagScope(): OrganizationFeatureFlagScope {
  return useContext(OrganizationFeatureFlagScopeContext)
}

export function FeatureFlagsProvider({ children }: { children: ReactNode }) {
  return (
    <PostHogProvider client={posthog}>
      <PostHogPageTemplateTracker />
      <OrganizationFeatureFlagScopeProvider>
        {children}
      </OrganizationFeatureFlagScopeProvider>
    </PostHogProvider>
  )
}
